import { Trans, useTranslation } from 'react-i18next'
import { ComposedChart, Bar, Cell, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { StaatidTrendPoint } from './api'
import { formatMonthName } from './api'
import { isPartialMonth, formatNo } from '../partialPeriod'

/** slate-400: readable on both light and dark card surfaces. */
const ANTALL_FILL = '#94a3b8'

interface Props {
  data: StaatidTrendPoint[]
  loading: boolean
  /** Newest date the data covers ("YYYY-MM-DD"), from useMaxDataDate. */
  maxDataDate?: string | null
}

export default function TrendChart({ data, loading, maxDataDate }: Props) {
  const { t } = useTranslation('staatid')
  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="chart-skeleton" className="animate-pulse h-56 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  // The nightly import means the current month is always incomplete, so its
  // bar is short for a calendar reason, not a commercial one. Left unmarked it
  // reads as a collapse in sales.
  const partial = data.find(p => isPartialMonth(p.maned, maxDataDate))

  return (
    <div className="card p-4" data-testid="staatid-trend-chart">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('trend.title')}</h3>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="maned" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="antall" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="median" orientation="right" tick={{ fontSize: 11 }} unit=" d" />
          <Tooltip formatter={(v: number) => v.toLocaleString('nb-NO')} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="antall" dataKey="antall" name={t('common.antallSluttsalg')} fill={ANTALL_FILL} fillOpacity={0.45} radius={[3, 3, 0, 0]}>
            {/* A partial month is drawn hollow — present, countable, and
                visibly not comparable to the full months beside it. */}
            {data.map(p => (
              <Cell
                key={p.maned}
                fill={isPartialMonth(p.maned, maxDataDate) ? 'transparent' : ANTALL_FILL}
                stroke={isPartialMonth(p.maned, maxDataDate) ? ANTALL_FILL : undefined}
                strokeWidth={isPartialMonth(p.maned, maxDataDate) ? 1.5 : undefined}
                strokeDasharray={isPartialMonth(p.maned, maxDataDate) ? '3 2' : undefined}
              />
            ))}
          </Bar>
          <Line
            yAxisId="median"
            type="monotone"
            dataKey="median_dager"
            name={t('common.medianDager')}
            stroke="#06b6d4"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      {partial && maxDataDate && (
        <p
          className="text-xs text-gray-500 dark:text-gray-400 mt-2"
          data-testid="staatid-trend-partial-note"
        >
          <Trans
            t={t}
            i18nKey="trend.partialNote"
            values={{ month: formatMonthName(partial.maned), date: formatNo(maxDataDate) }}
            components={{ b: <strong /> }}
          />
        </p>
      )}
    </div>
  )
}
